import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { Button, Divider, Drawer, IconButton, TextField } from '@mui/material'
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useState } from 'react'

import Dialog from '../common/dialog'
import Loader from '../common/loader'
import { handleCheckIfOtpCodeIsValid } from '../../utils'
import { FormError, Payment, SupportTicket } from '../../types'
import { AuthContext, NotificationsContext } from '../../contexts'
import CloseModalIcon from '../../assets/icons/close-modal-icon.svg'
import { checkTwoFactorAuth, requestAccessToken } from '../../api/auth.api'
import { sendPaymentComplianceComment, sendReviewedPaymentByCompliance, rejectPaymentByCompliance } from '../../api/payments.api'
import { baseButtonBlueBg, baseButtonSuccess, baseButtonWarning, baseButtonWhiteBg, textFieldWhiteBg } from '../../theme/custom-styles'

const drawerWidth = 600

export const ComplianceResolutionModule = ({
  paymentData,
  drawerOpened,
  setDrawerOpened,
  paymentTicketsData,
  handleOpenRejectPaymentModal
}: {
  drawerOpened: boolean
  paymentTicketsData?: Array<SupportTicket>
  handleOpenRejectPaymentModal?: () => void
  paymentData: Payment | Partial<Payment> | null
  setDrawerOpened: Dispatch<SetStateAction<boolean>>
}) => {
  const navigate = useNavigate()
  const { paymentId } = useParams()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['payments-to-review-management'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [otpCode, setOtpCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<FormError>({})
  const [decisionToTake, setDecisionToTake] = useState('')
  const [paymentActionReason, setPaymentActionReason] = useState('')
  const [complianceCommentToSend, setComplianceCommentToSend] = useState('')
  const [paymentComplianceCommentsData, setPaymentComplianceCommentsData] = useState<Array<any>>([])
  const [openSetPaymentStatusConfimationModal, setOpenSetPaymentStatusConfimationModal] = useState(false)
  const [dialogConfirmRejectPaymentIsOpen, setDialogConfirmRejectPaymentIsOpen] = useState<boolean>(false)
  const [paymentStatusConfimationModalIsLoading, setPaymentStatusConfimationModalIsLoading] = useState(false)
  const [dialogConfirmRejectPaymentIsLoading, setDialogConfirmRejectPaymentIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (Array.isArray(paymentTicketsData) && paymentTicketsData.length > 0 && paymentTicketsData[0].complianceComments) {
      const complianceCommentsToDisplay = paymentTicketsData[0].complianceComments ? [...paymentTicketsData[0].complianceComments] : []
      complianceCommentsToDisplay.sort((a: any, b: any) => {
        const thisCommentCreatedAt = a.date instanceof Date ? a.date.toISOString() : a.date
        const nextCommentCreatedAt = b.date instanceof Date ? b.date.toISOString() : b.date

        if (thisCommentCreatedAt && nextCommentCreatedAt) {
          return Date.parse(nextCommentCreatedAt) - Date.parse(thisCommentCreatedAt)
        }

        return 0
      })

      setPaymentComplianceCommentsData(complianceCommentsToDisplay)
    }
  }, [paymentTicketsData])

  const handleRejectPayment = async () => {
    try {
      setErrors({})

      if (!paymentActionReason) {
        setErrors({
          paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField')
        })
        return
      }

      setOpenSetPaymentStatusConfimationModal(true)

      /* setDialogConfirmRejectPaymentIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await returnPaymentData(String(paymentData?._id), { paymentActionReason }, accessToken)
      const {
        data: { paymentTickets }
      } = await getPaymentTickets(String(paymentData?._id), accessToken)

      setPaymentTicketsData(paymentTickets)
      setDialogConfirmRejectPaymentIsLoading(false)
      setDialogConfirmReturnPaymentIsOpen(false)
      updateNewNotificationStatus?.(true, 'payment-rejected-success') */
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogConfirmRejectPaymentIsLoading(false)
    }
  }

  const handleRejectPaymentConfirmed = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setPaymentStatusConfimationModalIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await rejectPaymentByCompliance(String(paymentId), { paymentActionReason }, accessToken)

      setDecisionToTake('')
      setPaymentActionReason('')
      setDialogConfirmRejectPaymentIsOpen(false)
      setOpenSetPaymentStatusConfimationModal(false)
      setPaymentStatusConfimationModalIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-status-updated-success')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
    }
  }

  const handleApprovePaymentConfirmed = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setPaymentStatusConfimationModalIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendReviewedPaymentByCompliance(String(paymentId), accessToken)

      setDecisionToTake('')
      setOpenSetPaymentStatusConfimationModal(false)
      setPaymentStatusConfimationModalIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-status-updated-success')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
    }
  }

  const handleAddComplianceCommentToPayment = async (askForSecondOpinion: boolean) => {
    try {
      setErrors({})

      if (!complianceCommentToSend) {
        setErrors({
          complianceCommentToSend: t('complianceProcess.commentFieldEmpty')
        })
        return
      }

      setIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { paymentTicketData }
      } = await sendPaymentComplianceComment({ paymentId: String(paymentData?._id), complianceCommentToSend, askForSecondOpinion }, accessToken)

      const complianceCommentsToDisplay = paymentTicketData.complianceComments ? [...paymentTicketData.complianceComments] : []
      complianceCommentsToDisplay.sort((a: any, b: any) => {
        const thisCommentCreatedAt = a.date instanceof Date ? a.date.toISOString() : a.date
        const nextCommentCreatedAt = b.date instanceof Date ? b.date.toISOString() : b.date

        if (thisCommentCreatedAt && nextCommentCreatedAt) {
          return Date.parse(nextCommentCreatedAt) - Date.parse(thisCommentCreatedAt)
        }

        return 0
      })

      setPaymentComplianceCommentsData(complianceCommentsToDisplay)
      updateNewNotificationStatus?.(true, 'payment-compliance-comment-added')
      setComplianceCommentToSend('')
      setIsLoading(false)
      console.log('askForSecondOpinion', askForSecondOpinion)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Drawer
      anchor="right"
      open={drawerOpened}
      sx={{
        position: 'relative',
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box'
        }
      }}
      onClose={() => setDrawerOpened(!drawerOpened)}
    >
      <div style={{ padding: '20px 20px 200px', boxSizing: 'border-box', position: 'relative' }}>
        {isLoading && <Loader />}
        <div className="dialog-title-container">
          <IconButton
            edge="end"
            aria-label="toggle password visibility"
            sx={{ color: '#525252' }}
            onClick={() => setDrawerOpened(false)}
          >
            <img
              src={CloseModalIcon}
              alt="close-modal-icon"
            />
          </IconButton>
          <div className="dialog-title">{t('complianceProcessTitle')}</div>
        </div>
        {paymentData && 'complianceResolutionData' in paymentData && (paymentData.complianceResolutionData?.approved || paymentData.complianceResolutionData?.rejected) && (
          <>
            {paymentData.complianceResolutionData?.approved && (
              <div style={{ marginTop: '10px', textAlign: 'center', color: 'rgb(34, 197, 94)', padding: '16px', boxSizing: 'border-box' }}>
                {t('paymentApprovedByCompliance', {
                  approvedByName: String(paymentData.complianceResolutionData?.approvedBy.name).toUpperCase(),
                  approvedDate: format(paymentData.complianceResolutionData?.decisionDate, 'dd/MM/yyyy | HH:mm:ss')
                })}
              </div>
            )}
            {paymentData.complianceResolutionData?.rejected && (
              <div style={{ marginTop: '10px', textAlign: 'center', color: 'rgb(245, 158, 11)', padding: '16px', boxSizing: 'border-box' }}>
                {t('paymentRejectedByCompliance', {
                  rejectedByName: String(paymentData.complianceResolutionData?.rejectedBy.name).toUpperCase(),
                  rejectedDate: format(paymentData.complianceResolutionData?.decisionDate, 'dd/MM/yyyy | HH:mm:ss')
                })}
              </div>
            )}
            {Array.isArray(paymentTicketsData) && (paymentTicketsData.length === 0 || (paymentTicketsData.length > 0 && paymentComplianceCommentsData.length === 0)) && (
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <div>{t('complianceProcess.paymentResolutionWithoutComments')}</div>
              </div>
            )}
          </>
        )}
        {Array.isArray(paymentTicketsData) && paymentTicketsData.length > 0 && paymentComplianceCommentsData.length > 0 && (
          <>
            {paymentComplianceCommentsData.map((paymentComplianceComment: any, index: number) => {
              return (
                <div
                  key={index}
                  style={
                    paymentComplianceComment.sentBy.indexOf('DIEGO') > -1
                      ? { padding: '20px', boxSizing: 'border-box', borderBottom: '1px solid #ccc', backgroundColor: 'rgba(245,158,11,0.1)' }
                      : { padding: '16px 0px', boxSizing: 'border-box', borderBottom: '1px solid #ccc' }
                  }
                >
                  <div style={{ color: '#737373', lineHeight: '150%' }}>
                    {paymentComplianceComment.description.split('\n').map((line: string, index: number) => (
                      <Fragment key={index}>
                        {line}
                        <br />
                      </Fragment>
                    ))}
                    <br />
                    <strong>{`${t('complianceProcess.sentBy')}:`}</strong>
                    {paymentComplianceComment.sentBy.indexOf('DIEGO') > -1
                      ? ` ${paymentComplianceComment.sentBy} | (Jefe de cumplimiento | ADAMO PAY)`
                      : ` ${paymentComplianceComment.sentBy} | (Equipo de cumplimiento | ADAMO PAY)`}
                    <br />
                    <strong>{`${t('complianceProcess.date')}:`}</strong> {`${format(new Date(paymentComplianceComment.date), 'dd/MM/yyyy | HH:mm:ss')}`}
                  </div>
                </div>
              )
            })}
          </>
        )}
        {paymentData &&
          (!('complianceResolutionData' in paymentData) || ('complianceResolutionData' in paymentData && !paymentData.complianceResolutionData?.approved && !paymentData.complianceResolutionData?.rejected)) && (
            <>
              <div style={{ width: '100%', marginTop: '20px', gap: '30px', display: 'flex', flexDirection: 'column' }}>
                <TextField
                  minRows={4}
                  fullWidth
                  multiline
                  value={complianceCommentToSend || ''}
                  error={!!errors.complianceCommentToSend}
                  helperText={errors.complianceCommentToSend}
                  label={t('complianceProcess.commentsTextFieldLabel')}
                  onChange={(event) => setComplianceCommentToSend(event.target.value)}
                />
              </div>
              <div style={{ width: '100%', marginTop: '20px' }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={baseButtonWhiteBg}
                  onClick={() => handleAddComplianceCommentToPayment(false)}
                >
                  {t('complianceProcess.commentsButtonText')}
                </Button>
              </div>
              <Divider sx={{ margin: '30px 0px' }} />
              <div style={{ display: 'flex', gap: '15px', padding: '20px 0px', justifyContent: 'center', boxSizing: 'border-box', width: '100%' }}>
                <Button
                  variant="text"
                  sx={{
                    ...baseButtonBlueBg,
                    '&.MuiButtonBase-root': {
                      color: 'rgb(18, 93, 86)',
                      fontSize: '13px',
                      boxShadow: 'none',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      backgroundColor: '#F6FEFC'
                    }
                  }}
                  onClick={() => handleAddComplianceCommentToPayment(true)}
                >
                  {t('contactSecondInstance')}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    ...baseButtonSuccess,
                    '&.MuiButtonBase-root': {
                      color: '#22C55E',
                      fontSize: '13px',
                      boxShadow: 'none',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      backgroundColor: '#F0FDF4'
                    }
                  }}
                  onClick={() => {
                    setDecisionToTake('approve')
                    setOpenSetPaymentStatusConfimationModal(true)
                  }}
                >
                  {t('approveRequest')}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    ...baseButtonWarning,
                    '&.MuiButtonBase-root': {
                      color: '#EF4444',
                      fontSize: '13px',
                      boxShadow: 'none',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      backgroundColor: '#FEF2F2'
                    }
                  }}
                  onClick={() => {
                    if (handleOpenRejectPaymentModal) {
                      handleOpenRejectPaymentModal()
                      return
                    }

                    setDecisionToTake('reject')
                    setDialogConfirmRejectPaymentIsOpen(true)
                  }}
                >
                  {t('rejectRequest')}
                </Button>
              </div>
            </>
          )}
      </div>
      {dialogConfirmRejectPaymentIsOpen && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={handleRejectPayment}
          dialogTitle={t('confirmRejectPaymentModal.title')}
          dialogIsLoading={dialogConfirmRejectPaymentIsLoading}
          actionButtonText={t('confirmRejectPaymentModal.actionButtonText')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setDecisionToTake('')
              setPaymentActionReason('')
            }

            setDialogConfirmRejectPaymentIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <div>{t('confirmRejectPaymentModal.instruction')}</div>
            <div style={{ padding: '0px 16px', boxSizing: 'border-box' }}>
              <TextField
                fullWidth
                multiline
                variant="standard"
                sx={textFieldWhiteBg}
                id="filled-textarea"
                value={paymentActionReason || ''}
                error={!!errors.paymentActionReason}
                helperText={errors.paymentActionReason}
                onChange={(event) => {
                  setErrors({})
                  setPaymentActionReason(event.target.value.toUpperCase())
                }}
                label={t('confirmSetBackgroundResearchReasonModal.placeholder')}
              />
            </div>
          </div>
        </Dialog>
      )}
      {openSetPaymentStatusConfimationModal && (
        <Dialog
          errors={errors}
          handleDialogAction={() => {
            if (decisionToTake === 'reject') {
              handleRejectPaymentConfirmed()
              return
            }

            handleApprovePaymentConfirmed()
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setDecisionToTake('')
            }

            setOpenSetPaymentStatusConfimationModal(openState)
          }}
          actionButtonWarningStyle={decisionToTake === 'reject'}
          actionButtonSuccessStyle={decisionToTake === 'approve'}
          dialogIsLoading={paymentStatusConfimationModalIsLoading}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          actionButtonText={
            decisionToTake === 'reject'
              ? t('confirmSendPaymentModal.rejectActionButtonText', { ns: 'payments-to-review-management' })
              : t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })
          }
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </Drawer>
  )
}
